import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { 
  Permissions,
  PermissionCategories,
  type PermissionCategory,
  type ModulePermissionComputed
} from '@/types/permissions';
import { 
  getUserPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canReadModule,
  canWriteModule,
  canDeleteModule,
  isAdmin,
  isReadOnly,
  permissionToMethodName,
  clearPermissionCache
} from '@/utils/permissionHelper';
import { useUserStore } from './user';

/**
 * Permission Store - Centralized permission management
 * Reactive state management for user permissions with caching
 */
export const usePermissionStore = defineStore('permissions', () => {
  // ===== State =====
  
  // Module cache - Her module() çağrısında yeniden hesaplamamak için
  const moduleCache = ref(new Map<PermissionCategory, any>());
  
  // Permission cache control
  const cacheEnabled = ref(true);
  const lastCacheUpdate = ref(Date.now());

  // ===== Getters (Computed) =====

  /**
   * User store'dan reactive olarak user roles alır
   */
  const userRoles = computed(() => {
    const userStore = useUserStore();
    return userStore.getUserRoles;
  });

  /**
   * Kullanıcının tüm permissionlarını reactive olarak döndürür
   */
  const allUserPermissions = computed(() => {
    const roles = userRoles.value || [];
    return getUserPermissions(roles);
  });

  /**
   * Kullanıcının admin olup olmadığını reactive olarak kontrol eder
   */
  const userIsAdmin = computed(() => {
    const roles = userRoles.value || [];
    return isAdmin(roles);
  });

  /**
   * Kullanıcının read-only olup olmadığını reactive olarak kontrol eder
   */
  const userIsReadOnly = computed(() => {
    const roles = userRoles.value || [];
    return isReadOnly(roles);
  });

  // ===== Core Permission Functions =====

  /**
   * Kullanıcının belirli bir permission'a sahip olup olmadığını kontrol eder
   */
  const can = (permission: Permissions): boolean => {
    return hasPermission(userRoles.value, permission);
  };

  /**
   * Kullanıcının verilen permissionlardan herhangi birine sahip olup olmadığını kontrol eder
   */
  const canAny = (permissions: Permissions[]): boolean => {
    return hasAnyPermission(userRoles.value, permissions);
  };

  /**
   * Kullanıcının verilen permissionların tümüne sahip olup olmadığını kontrol eder
   */
  const canAll = (permissions: Permissions[]): boolean => {
    return hasAllPermissions(userRoles.value, permissions);
  };

  /**
   * Kullanıcının belirli bir modülü okuyup okuyamayacağını kontrol eder
   */
  const canRead = (moduleKey: PermissionCategory): boolean => {
    return canReadModule(userRoles.value, moduleKey);
  };

  /**
   * Kullanıcının belirli bir modülde yazma yapıp yapamayacağını kontrol eder
   */
  const canWrite = (moduleKey: PermissionCategory): boolean => {
    return canWriteModule(userRoles.value, moduleKey);
  };

  /**
   * Kullanıcının belirli bir modülde silme yapıp yapamayacağını kontrol eder
   */
  const canDelete = (moduleKey: PermissionCategory): boolean => {
    return canDeleteModule(userRoles.value, moduleKey);
  };

  // ===== Dynamic Module Factory =====

  /**
   * Dynamic module factory - Cached & Type-safe & Reactive
   * Store-based caching for better performance across components
   */
  const module = <T extends PermissionCategory>(moduleKey: T): ModulePermissionComputed<T> => {
    // Cache check
    if (cacheEnabled.value && moduleCache.value.has(moduleKey)) {
      return moduleCache.value.get(moduleKey);
    }
    
    const modulePermissions = PermissionCategories[moduleKey];
    if (!modulePermissions) {
      console.warn(`[PermissionStore] Module ${moduleKey} not found in PermissionCategories`);
      return {} as ModulePermissionComputed<T>;
    }
    
    const result = {} as any;
    
    // Her permission için computed property oluştur
    modulePermissions.forEach(permission => {
      const methodName = permissionToMethodName(permission);
      
      // Computed property - store'daki userRoles değiştiğinde otomatik güncellenir
      result[methodName] = computed(() => can(permission));
    });
    
    // Cache'e kaydet - performance için
    if (cacheEnabled.value) {
      moduleCache.value.set(moduleKey, result);
    }
    
    return result as ModulePermissionComputed<T>;
  };

  // ===== Module Permission Helpers =====

  /**
   * Belirli bir modülün tüm permission durumunu döndürür
   */
  const getModulePermissions = (moduleKey: PermissionCategory) => {
    return {
      canRead: canRead(moduleKey),
      canWrite: canWrite(moduleKey),
      canDelete: canDelete(moduleKey),
    };
  };

  /**
   * Tüm modüllerin permission durumunu döndürür (debug/admin için)
   */
  const getAllModulePermissions = () => {
    const result: Record<string, any> = {};
    
    Object.keys(PermissionCategories).forEach(moduleKey => {
      const key = moduleKey as PermissionCategory;
      result[key] = getModulePermissions(key);
    });
    
    return result;
  };

  // ===== Cache Management =====

  /**
   * Cache'leri temizler ve yeniden oluşturmaya zorlar
   */
  const clearCache = () => {
    console.log('[PermissionStore] Clearing cache...');
    
    // Local module cache
    moduleCache.value.clear();
    
    // Permission helper cache
    clearPermissionCache();
    
    // Update timestamp
    lastCacheUpdate.value = Date.now();
  };

  /**
   * Cache'i enable/disable eder
   */
  const setCacheEnabled = (enabled: boolean) => {
    cacheEnabled.value = enabled;
    if (!enabled) {
      clearCache();
    }
  };

  /**
   * Cache istatistiklerini döndürür
   */
  const getCacheStats = () => {
    return {
      modulesCached: moduleCache.value.size,
      cacheEnabled: cacheEnabled.value,
      lastUpdate: lastCacheUpdate.value,
      userRoles: userRoles.value.length,
      totalPermissions: allUserPermissions.value.length
    };
  };

  // ===== Debug & Development =====

  /**
   * Debug için - kullanıcının permission durumunu log'lar
   */
  const debugPermissions = () => {
    console.group('🔐 [PermissionStore] Debug Information');
    console.log('👤 User Roles:', userRoles.value.map(r => r.roleName));
    console.log('🔑 All Permissions:', allUserPermissions.value);
    console.log('👑 Is Admin:', userIsAdmin.value);
    console.log('👀 Is Read Only:', userIsReadOnly.value);
    console.log('💾 Cache Stats:', getCacheStats());
    console.log('📊 All Module Permissions:', getAllModulePermissions());
    console.groupEnd();
  };

  /**
   * Belirli bir permission'ın hangi rollerden geldiğini analiz eder
   */
  const analyzePermission = (permission: Permissions) => {
    const result = {
      permission,
      hasAccess: can(permission),
      grantingRoles: [] as string[],
      userRoles: userRoles.value.map(r => r.roleName)
    };

    // Her rolü kontrol et
    userRoles.value.forEach(role => {
      const rolePermissions = getUserPermissions([role]);
      if (rolePermissions.includes(permission)) {
        result.grantingRoles.push(role.roleName);
      }
    });

    return result;
  };

  // ===== Watchers & Reactive Updates =====

  // User roles değiştiğinde cache'i otomatik temizle
  // Not: Bu watcher setup fonksiyonu içinde olduğu için otomatik çalışır
  let previousRoles: string[] = [];
  
  const watchUserRoles = () => {
    const currentRoles = userRoles.value.map(r => r.roleName).sort();
    const prevRoles = previousRoles.sort();
    
    // Roles changed?
    if (JSON.stringify(currentRoles) !== JSON.stringify(prevRoles)) {
      console.log('[PermissionStore] User roles changed, clearing cache');
      clearCache();
      previousRoles = currentRoles;
    }
  };

  // Vue'nun reactivity sistemini kullanarak otomatik watch
  // computed(() => watchUserRoles());

  // ===== Store Return =====

  return {
    // ===== State =====
    userRoles,
    allUserPermissions,
    userIsAdmin,
    userIsReadOnly,

    // ===== Core Functions =====
    can,
    canAny,
    canAll,
    canRead,
    canWrite,
    canDelete,

    // ===== Module Factory =====
    module,

    // ===== Helpers =====
    getModulePermissions,
    getAllModulePermissions,

    // ===== Cache Management =====
    clearCache,
    setCacheEnabled,
    getCacheStats,

    // ===== Debug & Analysis =====
    debugPermissions,
    analyzePermission,
  };
});